// userService.ts - 사용자 프로필 관리
import { useAuthStore } from '@/stores/authStore';
import axiosInstance from '@/utils/axios';
import { StandardApiResponse, ProfileData } from '@/types/auth_types';
import { logger } from '@/utils/logger';

interface ProfileResponse {
  uid: string;
  email: string;
  full_name: string;
  provider?: 'email' | 'google' | 'kakao';
}

interface UpdateNameResponse {
  uid: number;
  email: string;
  full_name: string;
  provider?: 'email' | 'google' | 'kakao';
}

/**
 * 프로필 조회
 * 중복 호출 방지 로직 포함
 */
export const getProfile = async (): Promise<StandardApiResponse<ProfileData>> => {
  // 중복 호출 방지 체크
  const { isProfileFetching } = useAuthStore.getState();
  if (isProfileFetching) {
    return {
      success: false,
      message: '프로필 조회가 이미 진행 중입니다.',
      data: null,
    };
  }

  try {
    // fetching 상태 설정
    useAuthStore.getState().setProfileFetching(true);

    const { data } = await axiosInstance.get<ProfileResponse>('/auth/user/');

    useAuthStore.getState().setProfileFetched(true);

    return {
      success: true,
      message: '프로필 조회 성공',
      data: {
        id: data.uid,
        email: data.email,
        name: data.full_name || data.email.split('@')[0],
        provider: data.provider || 'email',
      },
    };
  } catch (error) {
    logger.error('프로필 조회 실패', { error });
    return {
      success: false,
      message: '프로필 조회 실패',
      data: null,
    };
  } finally {
    // fetching 상태 해제
    useAuthStore.getState().setProfileFetching(false);
  }
};

/**
 * 이름 수정
 * 응답 형식: { uid: number, email: string, full_name: string }
 */
export const updateName = async (full_name: string): Promise<StandardApiResponse<ProfileData>> => {
  try {
    const response = await axiosInstance.put<UpdateNameResponse>('/auth/user/', { full_name });

    return {
      success: true,
      message: '이름이 성공적으로 변경되었습니다.',
      data: {
        id: String(response.data.uid),
        email: response.data.email,
        name: response.data.full_name,
        provider: response.data.provider || 'email',
      },
    };
  } catch (error) {
    logger.error('이름 변경 실패', { error, full_name });
    return {
      success: false,
      message: '이름 변경에 실패했습니다.',
      data: null,
    };
  }
};

/**
 * 비밀번호 변경
 */
export const changePassword = async (new_password1: string, new_password2: string) => {
  const { data } = await axiosInstance.post('/auth/password/change/', {
    new_password1,
    new_password2,
  });
  return data;
};

/**
 * 회원 탈퇴
 */
export const deleteAccount = async (
  confirmDelete: boolean,
): Promise<StandardApiResponse<unknown>> => {
  try {
    const { data } = await axiosInstance.delete('/users/account', {
      data: { confirmDelete },
    });

    if (data && typeof data === 'object' && 'success' in data) {
      return data as StandardApiResponse<unknown>;
    }

    return {
      success: true,
      message: '회원 탈퇴가 완료되었습니다.',
      data: null,
    };
  } catch (error) {
    return {
      success: false,
      message: '회원 탈퇴 중 오류가 발생했습니다.',
      data: null,
    };
  }
};
